// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  Inject,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { firstValueFrom, map } from "rxjs";

import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { EnvironmentService } from "@bitwarden/common/platform/abstractions/environment.service";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { SendDisabledReason } from "@bitwarden/common/tools/models/send-disabled-reason";
import { WhoCanAccessType } from "@bitwarden/common/tools/models/send-who-can-access-type";
import { SendView } from "@bitwarden/common/tools/send/models/view/send.view";
import { SendApiService } from "@bitwarden/common/tools/send/services/send-api.service.abstraction";
import { AuthType } from "@bitwarden/common/tools/send/types/auth-type";
import { SendType } from "@bitwarden/common/tools/send/types/send-type";
import {
  DIALOG_DATA,
  DialogRef,
  AsyncActionsModule,
  ButtonModule,
  DialogService,
  IconButtonModule,
  SearchModule,
  ToastService,
  DialogModule,
  ButtonComponent,
  CalloutComponent,
} from "@bitwarden/components";
import { AlgorithmInfo } from "@bitwarden/generator-core";
import {
  DefaultSendFormConfigService,
  SendFormConfig,
  SendFormModule,
  SendFormService,
  SendItemDialogParams,
  SendItemDialogResult,
  SendPolicyService,
} from "@bitwarden/send-ui";
import { I18nPipe } from "@bitwarden/ui-common";
import { CipherFormGeneratorComponent } from "@bitwarden/vault";

import { BvDeleteSendDialogComponent } from "../shared/bv-delete-send-dialog.component";
import { BvSendFavoriteService } from "../shared/bv-send-favorite.service";

import { BvSendFormComponent } from "./bv-send-form.component";

@Component({
  templateUrl: "./bv-send-add-edit-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "bv-send-dialog-host",
  },
  imports: [
    CommonModule,
    SearchModule,
    I18nPipe,
    FormsModule,
    ButtonModule,
    IconButtonModule,
    SendFormModule,
    AsyncActionsModule,
    DialogModule,
    CipherFormGeneratorComponent,
    CalloutComponent,
    BvSendFormComponent,
  ],
  providers: [DefaultSendFormConfigService],
})
export class BvSendAddEditDialogComponent {
  readonly SendDisabledReason = SendDisabledReason;

  protected readonly disabledSendConfig = signal<{
    title: string;
    message: string;
    showMakeCopyButton: boolean;
  } | null>(null);

  readonly sendFormComponent = viewChild(BvSendFormComponent);
  readonly submitBtn = viewChild<ButtonComponent>("submitBtn");

  readonly headerText = computed(() => {
    const config = this.config();
    if (this.showGenerator()) {
      return "passwordGenerator";
    }
    if (!config) {
      return "createSend";
    }
    if (config.mode === "add") {
      return "createSend";
    }
    let sendAction: "view" | "edit" = "view";
    if (this.editing()) {
      sendAction = "edit";
    }
    const translation = {
      [SendType.Text]: {
        view: "viewTextSendHeader",
        edit: "editItemHeaderTextSendV2",
      },
      [SendType.File]: {
        view: "viewFileSendHeader",
        edit: "editItemHeaderFileSendV2",
      },
    };
    return translation[config.sendType][sendAction];
  });

  protected readonly config = signal<SendFormConfig | undefined>(undefined);
  readonly disableForm = false;
  protected readonly editing = signal(false);
  readonly showGenerator = signal(false);
  readonly generatedValue = signal("");
  readonly generatorButtonLabel = signal<string | undefined>(undefined);
  protected readonly showCopyButton = signal(false);
  protected readonly showTrashIconButton = signal(false);
  protected readonly isFavorite = signal(false);
  protected readonly favoritePending = signal(false);

  protected readonly favoriteIcon = computed(() =>
    this.isFavorite() || this.favoritePending() ? "bwi-star-f" : "bwi-star",
  );

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  constructor(
    @Inject(DIALOG_DATA) protected readonly params: SendItemDialogParams,
    private readonly dialogRef: DialogRef<SendItemDialogResult>,
    private readonly i18nService: I18nService,
    private readonly sendApiService: SendApiService,
    private readonly toastService: ToastService,
    private readonly dialogService: DialogService,
    private readonly sendFormService: SendFormService,
    private readonly sendPolicyService: SendPolicyService,
    private readonly platformUtilsService: PlatformUtilsService,
    private readonly environmentService: EnvironmentService,
    private readonly accountService: AccountService,
    private readonly bvSendFavoriteService: BvSendFavoriteService,
  ) {
    void this.init();
  }

  async init() {
    this.config.set(this.params.formConfig);
    const config = this.config();
    if (!config) {
      return;
    }
    if (config.originalSend) {
      const sendDisabledReason = await this.sendPolicyService.sendDisabledReason(
        config.originalSend,
      );
      if (sendDisabledReason === SendDisabledReason.RestrictedType) {
        this.disabledSendConfig.set({
          title:
            config.originalSend.type === SendType.Text
              ? "orgDoesNotAllowTextSends"
              : "orgDoesNotAllowFileSends",
          message: "sendWillAutomaticallyExpire",
          showMakeCopyButton: false,
        });
      } else if (sendDisabledReason === SendDisabledReason.Other) {
        this.disabledSendConfig.set({
          title: "sendNotCompliantWithYourOrgsPolicy",
          message: "sendDisabledNonCompliantBannerMessage",
          showMakeCopyButton: true,
        });
      } else {
        this.disabledSendConfig.set(null);
      }
    }
    this.editing.set(config.mode === "add");
    this.showCopyButton.set(
      config.originalSend?.disabled && config.originalSend?.type === SendType.Text,
    );
    this.showTrashIconButton.set(
      this.showCopyButton() || (!config.originalSend?.disabled && config.mode !== "add"),
    );

    if (config.originalSend?.id) {
      this.isFavorite.set(await this.bvSendFavoriteService.isFavorite(config.originalSend.id));
    } else {
      this.isFavorite.set(false);
      this.favoritePending.set(false);
    }
    this.changeDetectorRef.markForCheck();
  }

  protected async toggleFavorite(): Promise<void> {
    const sendId = this.config()?.originalSend?.id;
    if (sendId) {
      this.isFavorite.set(await this.bvSendFavoriteService.toggleFavorite(sendId));
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.favoritePending.update((pending) => !pending);
    this.changeDetectorRef.markForCheck();
  }

  openGenerator() {
    this.showGenerator.set(true);
    this.dialogRef.disableClose = true;
  }

  useGeneratedPassword() {
    const value = this.generatedValue();
    if (value) {
      this.sendFormComponent()?.sendDetailsComponent()?.setGeneratedPassword(value);
    }
    this.showGenerator.set(false);
    this.generatedValue.set("");
    this.dialogRef.disableClose = false;
  }

  closeGenerator() {
    this.showGenerator.set(false);
    this.generatedValue.set("");
    this.dialogRef.disableClose = false;
  }

  onValueGenerated(value: string) {
    this.generatedValue.set(value);
  }

  onAlgorithmSelected(selected?: AlgorithmInfo) {
    if (selected) {
      this.generatorButtonLabel.set(selected.useGeneratedValue);
    } else {
      this.generatorButtonLabel.set(this.i18nService.t("useThisPassword"));
    }
    this.generatedValue.set("");
  }

  async onSendCreated(send: SendView) {
    if (this.favoritePending()) {
      await this.bvSendFavoriteService.setFavorite(send.id, true);
    }
    if (this.sendFormComponent()?.sendDetailsComponent()?.copyLinkOnSave()) {
      const userId = await firstValueFrom(
        this.accountService.activeAccount$.pipe(map((a) => a?.id)),
      );
      const env = await firstValueFrom(this.environmentService.getEnvironment$(userId));
      const link = env.getSendUrl() + send.accessId + "/" + send.urlB64Key;
      this.platformUtilsService.copyToClipboard(link);
      this.toastService.showToast({
        variant: "success",
        title: null,
        message: this.i18nService.t("valueCopied", this.i18nService.t("sendLink")),
      });
    }
    await this.dialogRef.close({ result: SendItemDialogResult.Created, send });
  }

  async onSendUpdated(send: SendView) {
    await this.dialogRef.close({ result: SendItemDialogResult.Updated, send });
  }

  async onSendDeleted() {
    await this.dialogRef.close({ result: SendItemDialogResult.Deleted });
    this.toastService.showToast({
      variant: "success",
      title: null,
      message: this.i18nService.t("deletedSend"),
    });
  }

  readonly deleteSend = async () => {
    const confirmed = await BvDeleteSendDialogComponent.open(
      this.dialogService,
      "deleteSendPermanentConfirmation",
    );

    if (!confirmed) {
      return;
    }

    try {
      await this.sendApiService.delete(this.config()?.originalSend?.id);
    } catch (e) {
      this.toastService.showToast({
        variant: "error",
        title: null,
        message: e.message,
      });
      return;
    }

    await this.onSendDeleted();
  };

  protected editSend() {
    this.editing.set(true);
  }

  protected async cancelEditSend() {
    if (this.config()?.mode === "add") {
      void this.dialogRef.close();
    } else {
      const proceed = await this.sendFormService.promptForUnsavedEdits();
      if (!proceed) {
        return;
      }
      this.editing.set(false);
    }
  }

  readonly cancelEditSendAction = async (): Promise<boolean> => {
    await this.cancelEditSend();
    return true;
  };

  static open(dialogService: DialogService, params: SendItemDialogParams) {
    return dialogService.open<
      SendItemDialogResult,
      SendItemDialogParams,
      BvSendAddEditDialogComponent
    >(BvSendAddEditDialogComponent, {
      data: params,
      closePredicate: params.closePredicate,
    });
  }

  static openDrawer(dialogService: DialogService, params: SendItemDialogParams) {
    return dialogService.openDrawer<
      SendItemDialogResult,
      SendItemDialogParams,
      BvSendAddEditDialogComponent
    >(BvSendAddEditDialogComponent, {
      data: params,
      closePredicate: params.closePredicate,
    });
  }

  async makeCopy() {
    const originalSendView = this.sendFormService.originalSendView();
    if (!originalSendView) {
      return;
    }
    const hideEmailDisabled = await firstValueFrom(this.sendPolicyService.disableHideEmail$);
    const whoCanAccess = await firstValueFrom(this.sendPolicyService.whoCanAccess$);
    this.config.set({
      areSendsAllowed: true,
      mode: "add",
      sendType: originalSendView.type,
      originalSend: null,
      presetSendFields: {
        name: originalSendView.name,
        text: originalSendView.text,
        maxAccessCount: originalSendView.maxAccessCount,
        hideEmail: !hideEmailDisabled && originalSendView.hideEmail,
        notes: originalSendView.notes,
        authType:
          whoCanAccess === WhoCanAccessType.SpecificPeople
            ? AuthType.Email
            : whoCanAccess === WhoCanAccessType.PasswordProtected
              ? AuthType.Password
              : AuthType.None,
      },
    });
    await this.init();
  }
}
